import { useEffect, useState } from 'react';

// The homepage's animation helpers: when to play, how numbers count, where
// things are. Every animated visual on the page uses these.

// A number easing from `from` to `to` over `ms` once `run` turns true
export function useCountUp(from, to, run, { ms = 900, instant = false } = {}) {
  const [value, setValue] = useState(from);
  useEffect(() => {
    if (!run) {
      setValue(from);
      return undefined;
    }
    if (instant) {
      setValue(to);
      return undefined;
    }
    let frame;
    const start = window.performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / ms);
      setValue(from + (to - from) * (1 - Math.pow(1 - t, 3)));
      if (t < 1) frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [from, to, run, ms, instant]);
  return value;
}

export const prefersReducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// The homepage header's height; nothing under it is seen
export const HEADER = 64;
// A chapter's title bar (Solo, Filmmaker, Teams). Once reached, each stays
// pinned under the header, stacked; nothing under them is seen either
export const CHAPTER_BAR = 56;
const GONE = 0.08; // below this share showing, an element counts as gone

// How much of `el` is really showing: on screen, and not under the header
// or the chapter bars pinned below it.
function shownShare(el) {
  const rect = el.getBoundingClientRect();
  const bottom = Math.min(rect.bottom, window.innerHeight);
  const covered = HEADER + document.querySelectorAll('[data-pinned-bar]').length * CHAPTER_BAR;
  const top = Math.max(rect.top, covered);
  const room = Math.min(rect.height, window.innerHeight - covered);
  return room > 0 ? Math.max(0, bottom - top) / room : 0;
}

// Whether the element is in view: true once `threshold` of it shows, false
// again only once it has all but gone. The homepage's animations play when
// this turns true and reset when it turns false, so each one replays when
// you come back to it.
export function usePlayOnView(ref, threshold = 0.5) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let frame = 0;
    const check = () => {
      frame = 0;
      const share = shownShare(el);
      setInView((was) => (was ? share >= GONE : share >= threshold));
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(check);
    };
    check();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.cancelAnimationFrame(frame);
    };
  }, [ref, threshold]);
  return inView;
}

// An element's position inside `stage`, ignoring transforms (a panel turned
// edge-on still has its place)
export function placeIn(el, stage) {
  let x = 0;
  let y = 0;
  let node = el;
  while (node && node !== stage) {
    x += node.offsetLeft;
    y += node.offsetTop;
    node = node.offsetParent;
  }
  return { x, y, w: el.offsetWidth, h: el.offsetHeight };
}
